/**
 * Formatting utility functions for the application
 */

/**
 * Format repository size from KB to human-readable format
 * @param sizeInKB - Size in kilobytes
 * @returns Formatted size string (e.g., "1.5 MB", "342 KB", "2.34 GB")
 */
export const formatRepoSize = (sizeInKB: number | undefined): string => {
  if (!sizeInKB) return 'Unknown';
  if (sizeInKB < 1024) return `${sizeInKB} KB`;
  if (sizeInKB < 1024 * 1024) return `${(sizeInKB / 1024).toFixed(1)} MB`;
  return `${(sizeInKB / (1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Calculate and format duration between two timestamps
 * @param startTime - Start timestamp (ISO string or null)
 * @param endTime - End timestamp (ISO string or null)
 * @returns Formatted duration (e.g., "45s", "2m 30s") or "N/A" if timestamps missing
 */
export const formatDuration = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string => {
  if (!startTime || !endTime) return 'N/A';
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const durationMs = end - start;
  const durationSec = Math.floor(durationMs / 1000);
  if (durationSec < 60) return `${durationSec}s`;
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  return `${minutes}m ${seconds}s`;
};

/**
 * Parse a date value, treating bare YYYY-MM-DD strings as a local calendar date.
 * `new Date("YYYY-MM-DD")` is spec'd to UTC midnight, which renders as the previous
 * day for users west of UTC — the source of the "one day behind" bug.
 */
export const parseDateOnly = (date: string | Date): Date => {
  if (date instanceof Date) return date;
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(date);
};

/**
 * Format a date to a localized string
 * @param date - Date to format (ISO string, Date object, or undefined)
 * @returns Formatted date string or "N/A" if date is missing
 */
export const formatDate = (date: string | Date | undefined): string => {
  if (!date) return 'N/A';
  return parseDateOnly(date).toLocaleDateString();
};

/**
 * Format a timestamp to a localized date and time string
 * @param timestamp - Timestamp to format (ISO string, Date object, or undefined)
 * @returns Formatted timestamp string or "N/A" if timestamp is missing
 */
export const formatDateTime = (timestamp: string | Date | undefined): string => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString();
};

/**
 * Format scan duration from seconds to human-readable format
 * @param seconds - Duration in seconds
 * @returns Formatted duration (e.g., "45s", "2m 30s") or "N/A" if undefined
 */
export const formatScanDuration = (seconds: number | undefined): string => {
  if (!seconds) return 'N/A';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Format duration from milliseconds to human-readable format
 * @param ms - Duration in milliseconds
 * @returns Formatted duration (e.g., "1.2s", "2m 30s", "250ms")
 */
export const formatDurationMs = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Format elapsed time from seconds to human-readable format
 * @param seconds - Elapsed time in seconds
 * @returns Formatted time (e.g., "30s", "5m 30s", "1h 25m")
 */
export const formatElapsedTime = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Format ISO timestamp to local time (HH:MM:SS)
 * @param isoString - ISO timestamp string
 * @returns Formatted time string (e.g., "14:30:05")
 */
export const formatTimeOnly = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

/**
 * Format a raw count into a compact short form: 999 → "999", 1200 → "1.2k", 1_500_000 → "1.5M"
 */
export const formatCount = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

/**
 * Compute a pass-rate percentage from passed/total check counts.
 * Returns null when total is 0 to let callers skip rendering.
 */
export const formatPassRate = (passed: number, total: number): number | null => {
  if (!total) return null;
  return Math.round((passed / total) * 100);
};

/**
 * Calculate elapsed time from start timestamp to now
 * @param startedAt - Start timestamp (ISO string)
 * @returns Elapsed time in milliseconds, or 0 if not started
 */
export const getElapsedMs = (startedAt: string | null | undefined): number => {
  if (!startedAt) return 0;
  return Date.now() - new Date(startedAt).getTime();
};

/**
 * Format estimated time remaining based on average duration
 * Uses server time for calculation to ensure sync with backend
 * @param startedAt - Start timestamp (ISO string)
 * @param avgDurationMs - Average duration in milliseconds
 * @param serverTime - Current server timestamp (ISO string) for elapsed calculation
 * @returns Formatted ETA string (e.g., "~15s remaining", "Almost done...")
 */
export const formatEstimatedTime = (
  startedAt: string | null | undefined,
  avgDurationMs: number | null | undefined,
  serverTime?: string | null,
): string => {
  if (!startedAt || !avgDurationMs || !serverTime) return '';
  const startTimeMs = new Date(startedAt).getTime();
  const serverTimeMs = new Date(serverTime).getTime();
  const elapsed = serverTimeMs - startTimeMs;
  const remaining = Math.max(0, avgDurationMs - elapsed);
  if (remaining <= 0) return 'Almost done...';
  const remainingSec = Math.ceil(remaining / 1000);
  if (remainingSec < 60) return `~${remainingSec}s remaining`;
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  return `~${minutes}m ${seconds}s remaining`;
};
